import { createClient } from "@supabase/supabase-js";

// VITE_ wins when it is set, so a developer can point the dashboard at a
// branch or a local stack from .env.local without touching the app's own
// EXPO_PUBLIC_ values — which the phone build reads from the same file.
const url = (import.meta.env.VITE_SUPABASE_URL ?? import.meta.env.EXPO_PUBLIC_SUPABASE_URL) as
  | string
  | undefined;
const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY ??
  import.meta.env.EXPO_PUBLIC_SUPABASE_ANON_KEY) as string | undefined;

if (!url || !anon) {
  throw new Error(
    "Missing Supabase URL or anon key. Set VITE_SUPABASE_URL and " +
      "VITE_SUPABASE_ANON_KEY in .env.local, or EXPO_PUBLIC_* in .env — see .env.example.",
  );
}

/**
 * What the reset link left in the address bar, read before the client can clear
 * it. These two statements have to run above `createClient`, which is the only
 * reason they live in this file.
 *
 * `detectSessionInUrl` strips the code — and the error — out of the URL as soon
 * as the client is built, which is right: nobody wants a one-time token sitting
 * in browser history. But it also means a link that failed looks exactly like a
 * fresh visit, so the page renders a sign-in form and appears to have ignored
 * you. That is the bug this reads around.
 */
const params =
  typeof window === "undefined"
    ? { q: new URLSearchParams(), h: new URLSearchParams() }
    : {
        q: new URLSearchParams(window.location.search),
        h: new URLSearchParams(window.location.hash.slice(1)),
      };

const errorCode = params.q.get("error_code") ?? params.h.get("error_code");
const errorText = params.q.get("error_description") ?? params.h.get("error_description");

export const linkError: string | null =
  errorCode === "otp_expired" || errorText?.toLowerCase().includes("expired")
    ? "That link has expired, or it has already been used. Ask for a new one below."
    : errorText
      ? errorText.replace(/\+/g, " ")
      : (params.q.get("error") ?? params.h.get("error"))
        ? "That link could not be used. Ask for a new one below."
        : null;

/**
 * A PKCE code arrived. If no session exists once the client has had its go at
 * exchanging it, the verifier was missing — which in practice means the link
 * was opened in a different browser from the one that asked for it.
 */
export const linkHadCode = params.q.has("code");

/**
 * The dashboard talks to Supabase as the signed-in employer and nothing more.
 *
 * It holds the same anon key the app ships, which is public by design: every
 * query is still decided by the row-level security policies in
 * supabase/migrations. There is no service-role key here and there must never
 * be one — it would sit in a JavaScript bundle anybody can read, and it bypasses
 * RLS entirely.
 *
 * `detectSessionInUrl` is on, unlike the app's client. On the phone a password
 * reset is handed to shifty:// and finished in the app; in a browser the tokens
 * arrive in the URL fragment and this client has to pick them up itself.
 */
export const supabase = createClient(url, anon, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: "pkce",
  },
});
